<template>
  <div
    :style="{ 'z-index': getPopupZindex }"
    :class="{ 'customer-note-popup': true, 'is-visible': isVisible }">
    <h2>{{ popupTitle }}</h2>

    <field type="full-width">
      <label slot="label">
        {{ $t('customerNotePopup.content') }}
      </label>
      <textarea
        :class="{ 'is-invalid': errors.indexOf('empty-text') > -1 }"
        v-model="text"
        slot="field"
        :disabled="loadingData"
        @keyup="clearErrors('empty-text')"></textarea>
      <small
        slot="note"
        class="note"
        v-if="errors.indexOf('empty-text') > -1">
        {{ $t('customerNotePopup.contentIsRequired') }}
      </small>
    </field>

    <field
      v-if="notificationFields && notificationFields[0]"
      type="full-width">
      <label slot="label">
        {{ $t('customerNotePopup.notificationField') }}
      </label>
      <select
        slot="field"
        :class="{ 'is-invalid': invalidNotificationField }"
        v-model="notification_field"
        @change="invalidNotificationField = false">
        <option
          :value="null"
          key="field-00">
          {{ $t('common.ui.none') }}
        </option>
        <option
          v-for="(field, index) of notificationFields"
          :value="field"
          :key="'field-' + index">
          {{ field }}
        </option>
      </select>

      <small
        v-if="invalidNotificationField"
        slot="note"
        class="note text-danger">
        {{ invalidNotificationFieldText }}
      </small>
    </field>

    <btn
      @click.native="saveNote"
      type="popup-left primary">
      {{ saveButtonLabel }}
    </btn>

    <btn
      @click.native="cancel"
      type="popup-right">
      {{ $t('common.ui.cancel') }}
    </btn>
  </div>
</template>

<script>
import ErrorUtils from './../helpers/ErrorUtils.js';
import PopupZindex from './../mixins/PopupZindex';

export default {
  name: 'customer-note-popup',
  mixins: [
    PopupZindex
  ],
  data () {
    return {
      id: 0,
      text: '',
      notification_field: null,
      errors: [],
      notificationFields: [],
      isVisible: false,
      loadingData: false,
      // internals
      invalidNotificationField: false,
      invalidNotificationFieldText: ''
    };
  },
  computed: {
    popupTitle () {
      if (this.id === 0) {
        return this.$t('customerNotePopup.titleAdd');
      }

      return this.$t('customerNotePopup.titleEdit');
    },
    saveButtonLabel () {
      if (this.id === 0) {
        return this.$t('customerNotePopup.addNote');
      }

      return this.$t('common.ui.saveChanges');
    }
  },
  mounted () {
    this.$bus.$on('show-customer-note-form', this.show);
  },
  methods: {
    show (data) {
      if (!data) {
        this.id = 0;
        this.customerID = 0;
        this.text = '';
        this.notification_field = null;
        this.errors = [];
        this.loadingData = false;
        this.isVisible = true;
        this.notificationFields = [];
        return;
      }

      this.id = data.id || 0;
      this.notification_field = data.notification_field || null;
      this.customerID = data.customerID;
      this.text = data.text || '';
      this.notificationFields = data.notificationFields || [];
      this.errors = [];
      this.loadingData = false;
      this.isVisible = true;
      this.increasePopupZindex();
    },
    hide () {
      this.isVisible = false;
      this.removePopupZindex();
    },
    saveNote () {
      this.loadingData = true;

      this.validate();

      if (this.errors.length) {
        this.loadingData = false;
        return;
      }

      let noteData = {
        note: this.text,
        notification_field: this.notification_field
      };

      if (this.id !== 0) {
        noteData.id = this.id;
      }

      if (this.customerID !== 0) {
        noteData.customer_id = this.customerID;
      }

      this.$http.post('/panel/customers/customer-save-note', noteData).then(response => {
        this.$bus.$emit('customer-notes-reload-needed');
        this.hide();
        this.loadingData = false;
      }).catch(error => {
        if (
          error &&
          error.response &&
          error.response.data &&
          error.response.data.errors &&
          error.response.data.errors.notification_field
        ) {
          this.invalidNotificationField = true;
          this.invalidNotificationFieldText = error.response.data.errors.notification_field.join(' ');
        } else {
          ErrorUtils.handleSubmitError(error, '/panel/customers/customer-save-note', this.$router, this.$bus, this.saveNote.bind(this));
        }
        this.loadingData = false;
      });
    },
    validate (response) {
      if (this.text.trim() === '') {
        this.errors.push('empty-text');
      }
    },
    clearErrors (errorsToClear) {
      errorsToClear = errorsToClear.split(',');

      for (let i = 0; i < errorsToClear.length; i++) {
        let pos = this.errors.indexOf(errorsToClear[i]);
        this.errors.splice(pos, 1);
      }
    },
    cancel () {
      this.hide();
    }
  },
  beforeDestroy () {
    this.$bus.$off('show-customer-note-form', this.show);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.customer-note-popup {
  background: $color-light;
  border-radius: $border-radius-small;
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, .125);
  display: none;
  left: 50%;
  max-width: 94%;
  padding: 3rem 4rem 8rem 4rem;
  position: fixed;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 480px;

  &.is-visible {
    display: block;
  }

  h2 {
    font-weight: 400;
    line-height: 1;
    margin: 0 0 2rem 0;
  }

  .button-popup-left {
    border-radius: 0 0 0 $border-radius-small;
  }

  .button-popup-right {
    border-radius: 0 0 $border-radius-small 0;
  }

  .field {
    flex-wrap: wrap;

    label {
      margin-bottom: .5rem;
      text-align: left;
    }

    label,
    textarea,
    select {
      display: block;
      width: 100%!important;
    }

    .note {
      padding-left: 0;
    }
  }
}

@media (max-width: 480px) {
  .customer-note-popup {
    padding: 1.5rem 1.5rem 5rem 1.5rem;

    h2 {
      font-size: 2rem;
      margin-bottom: 1rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/CustomerNotePopup.vue